import {
  type LineItemPromotion,
  type ProductCampaignsPromotion,
} from '@/features/shop/services/Product/types'
import { Body } from '@shipt/design-system-typography'
import { ChevronRightIcon } from '@shipt/design-system-icons'
import styled from 'styled-components'
import { getRetailerFeaturedPromotionsUrl } from '@shared/constants/routes'
import { useRouteToSearch } from '@/features/shared/hooks/useRouteToSearch'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { FlagBadge } from '@/features/shared/elements/Badges'
import { useCampaignPromotions } from '@/features/shop/services/Campaigns/hooks'
import {
  getFeaturedPromoCardProperties,
  trackPromoCardClicked,
  trackPromoCardViewed,
} from '@/features/shared/analytics/promoCard'
import { useIsGuestWithZipOnly } from '@/features/shop/components/ProductDetail/PLA/hooks'
import { Column } from '@shipt/design-system-layouts'
import { useUserStoreName } from '@/features/account/services/User/hooks'
import { logWarn } from '@/features/shared/utils/logger'

type Props = {
  promotion: ProductCampaignsPromotion | LineItemPromotion
  description?: string | null
}

export const ProductDetailPromotionCallout = ({ promotion, description }: Props) => {
  const { routeToSearch } = useRouteToSearch()
  const { display_title, category_id, member_only } = promotion
  const { promotions: availablePromotions } = useCampaignPromotions()
  const isGuestWithZipOnly = useIsGuestWithZipOnly()
  const statePromo = availablePromotions.find(
    (promo) => promo.promotion_params?.category_id === category_id,
  )
  const userStore = useUserStoreName()

  const handleCalloutTitleClick = () => {
    const categoryId = category_id ?? 0
    if (!categoryId) {
      logWarn('categoryId is undefined in ProductDetailPromotionCallout. Invalid Url generated')
    }
    routeToSearch({
      params: { promotion_category_id: categoryId },
      pathname: getRetailerFeaturedPromotionsUrl(userStore, categoryId),
    })
    if (statePromo) {
      trackPromoCardClicked(getFeaturedPromoCardProperties({ promotion: statePromo }))
    }
  }
  const handleInView = () => {
    if (statePromo) {
      trackPromoCardViewed(getFeaturedPromoCardProperties({ promotion: statePromo }))
    }
  }

  const Content = (
    <Column spacing="xs" align="start">
      {!!member_only && <FlagBadge type="memberOnlyPromo" />}
      <Body size="md" strong concept="feedback" feedbackType="promotion">
        {display_title}
      </Body>
      <Body size="sm">{description ?? 'Mix and match eligible items'}</Body>
    </Column>
  )

  return (
    <ScrollIntoView onInView={handleInView}>
      {isGuestWithZipOnly ? (
        <CalloutWrapper>{Content}</CalloutWrapper>
      ) : (
        <CalloutButton
          as="button"
          onClick={handleCalloutTitleClick}
          aria-label={`View ${display_title} promotion`}
        >
          {Content}
          <RightArrow size="md" inherit />
        </CalloutButton>
      )}
    </ScrollIntoView>
  )
}

const CalloutWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border-radius: 0.5rem;
  border: none;
  padding: 0.25rem 0.5rem;
  min-height: 48px;
  background-color: ${({ theme }) => theme.gray50};
  color: ${({ theme }) => theme.gray600};
  box-sizing: border-box;
`

const CalloutButton = styled(CalloutWrapper)`
  cursor: pointer;
`
const RightArrow = styled(ChevronRightIcon)`
  flex-shrink: 0;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
`
